import React, { useEffect, useState } from 'react';
import { Box, Flex, Loader, TabsList, Text } from '@mantine/core';
import { LogoWithIcon } from 'src/UI/app-container/header/Header';
import { Tab, TabPanel, Tabs } from 'src/components';
import SignupTabContent from 'src/auth/login/SignupTabContent';
import LoginTabContent from 'src/auth/login/LoginTabContent';
import { Configuration, FrontendApi, LoginFlow } from '@ory/client';
import Config from 'src/common/Config';
import { useSearchParams } from 'react-router-dom';
import { Logger } from 'src/utils/Logger';
import { AuthPageLayout } from 'src/auth/components/AuthPageLayout';

const ory = new FrontendApi(
  new Configuration({
    basePath: Config.getAuthBasePath(),
    baseOptions: {
      withCredentials: true, // important for cookies
    },
  }),
);

const LOGIN_TABS = {
  LOGIN: 'LogIn',
  SIGNUP: 'SignUp',
};

const AUTH_METHOD_LINK_RECOVERY = 'link_recovery';

export const LoginPage = () => {
  const [flow, setFlow] = useState<LoginFlow>();
  const [flowError, setFlowError] = useState<string | undefined>(undefined);
  const [searchParams] = useSearchParams();
  const flowId = searchParams.get('flow');

  useEffect(() => {
    const initializeFlow = async () => {
      if (flowId) {
        try {
          const { data } = await ory.getLoginFlow({ id: flowId });
          setFlow(data);
        } catch (e) {
          setFlowError((e as Error).message);
          Logger.error('Could not get login flow.', e);
        }
        return;
      }

      try {
        const { data: session } = await ory.toSession();
        const authMethods = session.authentication_methods ?? [];
        const mostRecentMethod = authMethods[authMethods.length - 1]?.method;

        if (mostRecentMethod === AUTH_METHOD_LINK_RECOVERY) {
          // Invite flow session - force logout so user must authenticate with proper credentials
          try {
            const { data: logoutFlow } = await ory.createBrowserLogoutFlow();
            await ory.updateLogoutFlow({ token: logoutFlow.logout_token });
          } catch {
            // Logout failed - proceed anyway
          }
        } else {
          // User has a proper session, redirect to app
          window.location.replace('/');
          return;
        }
      } catch {
        // No session - proceed to create login flow
      }

      try {
        const { data } = await ory.createBrowserLoginFlow();
        setFlow(data);
      } catch (e) {
        setFlowError((e as Error).message);
        Logger.error('Could not initialize login flow.', e);
      }
    };

    initializeFlow();
  }, [flowId]);

  const resetFlow = async () => {
    try {
      const response = await ory.createBrowserLoginFlow();
      setFlow(response.data);
    } catch (e) {
      setFlowError('Could not reset login flow.');
      Logger.error('Could not reset login flow.', e);
    }
  };

  const renderLoginContent = () => {
    if (flow) {
      return (
        <LoginTabContent
          resetFlow={resetFlow}
          flow={flow}
          setError={setFlowError}
          setFlow={setFlow}
        />
      );
    }

    if (flowError) {
      return (
        <Box>
          <Text c="red" mt={'xl'}>
            Something went wrong trying to start the sign in flow. Please refresh the page and
            contact support if the problem persists.
          </Text>
        </Box>
      );
    }

    return (
      <Flex w={'100%'} justify={'center'} mt={'xl'}>
        <Loader />
      </Flex>
    );
  };

  return (
    <AuthPageLayout withPaper>
      <Box mb="xxl">
        <LogoWithIcon textColor="gray.10" />
      </Box>

      <Tabs variant="secondary" defaultValue="LogIn">
        <TabsList>
          <Tab value={LOGIN_TABS.LOGIN}>Log in</Tab>
          <Tab value={LOGIN_TABS.SIGNUP}>Sign up</Tab>
        </TabsList>

        <TabPanel value={LOGIN_TABS.LOGIN}>{renderLoginContent()}</TabPanel>
        <TabPanel value={LOGIN_TABS.SIGNUP}>
          <SignupTabContent />
        </TabPanel>
      </Tabs>
    </AuthPageLayout>
  );
};
